<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Messages</h1>
            <v-btn @click="showNewMessageDialog = true" color="primary" class="pull-right">New Message</v-btn>
            <p class="lead">See messages from your pupils.</p>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel">
                <table>
                    <thead>
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Subject</th>
                            <th>Message</th>
                            <th>Received</th>
                            <th class="options">&nbsp;</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            v-for="m in sortedMessages"
                            :key="m.id"
                            :class="{ unread_msg: !m.msg_read && m.to_teacher }"
                        >
                            <td>{{ m.to_teacher ? pupilName(m.pupil) : 'me' }}</td>
                            <td>{{ !m.to_teacher ? pupilName(m.pupil) : 'me' }}</td>
                            <td>{{ m.context }}</td>
                            <td>
                                <RouterLink :to="`/teacher/message/${m.id}`">{{ ellipsis(m.message, 30) }}</RouterLink>
                            </td>
                            <td>{{ formatDateTime(m.sent) }}</td>
                            <td class="d-flex">
                                <v-btn size="small" variant="outlined" :to="`/teacher/message/${m.id}`">View</v-btn>
                                <v-btn
                                    icon="mdi-trash-can-outline"
                                    variant="plain"
                                    size="small"
                                    color="error"
                                    class="ml-2"
                                    @click="deleteMessage(m.id)"
                                ></v-btn>
                            </td>
                        </tr>
                        <tr v-if="messages.length == 0">
                            <td colspan="5">No messages</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
    <v-dialog v-model="showNewMessageDialog" max-width="600px">
        <v-card title="New Message">
            <v-card-text>
                <form @submit.stop.prevent="sendMessage">
                    <div class="mb-3">
                        <label for="pupilSelect" class="form-label">To Pupil</label>
                        <v-select
                            v-model="selectedPupilUid"
                            id="pupilSelect"
                            :items="sortedPupils"
                            item-title="name"
                            item-value="uid"
                            required
                        ></v-select>
                    </div>
                    <div class="mb-3">
                        <label for="subjectText" class="form-label">Subject</label>
                        <v-text-field v-model="subjectText" id="subjectText" name="subjectText" required></v-text-field>
                    </div>
                    <div class="mb-3">
                        <label for="messageText" class="form-label">Message</label>
                        <v-textarea v-model="newMessageText" id="messageText" name="messageText" required></v-textarea>
                    </div>
                    <div>
                        <v-btn type="submit" color="primary">Send</v-btn>
                    </div>
                </form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { Message } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'messages' } });
const store = useTeacherStore();
store.fetchMessages();
store.fetchSchool();
store.fetchPupils();
store.fetchTeacher();
const { messages, pupils, teacher, school } = storeToRefs(store);
const { pupilName } = store;
const router = useRouter();

const { formatDateTime } = useDateFormat();
const confirm = useConfirmDialog();

const showNewMessageDialog = ref(false);
const selectedPupilUid = ref<string>();
const newMessageText = ref('');
const subjectText = ref('');

watch(showNewMessageDialog, () => {
    if (showNewMessageDialog.value) {
        selectedPupilUid.value = undefined;
        newMessageText.value = '';
        subjectText.value = '';
    }
});

const sortedMessages = computed(() => {
    const copy: Message[] = JSON.parse(JSON.stringify(messages.value));
    copy.sort((a, b) => {
        return b.sent.localeCompare(a.sent);
    });
    return copy;
});

const sortedPupils = computed(() => {
    const copy = JSON.parse(JSON.stringify(pupils.value));
    copy.sort((a: any, b: any) => {
        return a.name.localeCompare(b.name);
    });
    return copy;
});

watchEffect(() => {
    if (school.value && school.value.type.abbrv !== 'PREM') {
        router.push('/teacher/tlc-upgrade');
    }
});

async function sendMessage() {
    if (!selectedPupilUid.value) {
        return;
    }
    await store.createMessage({
        pupil: { uid: selectedPupilUid.value },
        teacher: { uid: teacher.value!.uid },
        message: newMessageText.value,
        context: subjectText.value,
        to_teacher: false,
    });
    showNewMessageDialog.value = false;
}

async function deleteMessage(messageId: number) {
    if (!(await confirm('Delete message', 'Are you sure you want to delete the message?'))) {
        return;
    }
    await store.deleteMessage(messageId);
}

function ellipsis(str: string, len: number) {
    if (!str) {
        return '...';
    }
    if (str.length <= len) {
        return str;
    }
    return str.substring(0, len) + '...';
}
</script>
<style scoped lang="scss">
td {
    vertical-align: middle !important;
}
</style>
